import React from 'react';
import { motion } from 'framer-motion';
import { Mic, Sparkles, DollarSign, UploadCloud } from 'lucide-react';
import { SectionHeading, Badge } from '../components/ui';
export const CreatorCompetitionPage = () => {
  return <div className="w-full pt-20 bg-charcoal min-h-screen" data-id="element-492">
      {/* HERO */}
      <section className="py-24 relative overflow-hidden" data-id="element-493">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-gold/10 via-dark to-dark" data-id="element-494"></div>
        <div className="container mx-auto px-4 relative z-10 text-center max-w-5xl" data-id="element-495">
          <Badge color="gold" className="mb-6" data-id="element-496">
            COMING SOON
          </Badge>
          <h1 className="text-6xl md:text-8xl font-black font-heading uppercase -skew-x-6 mb-8 text-gold text-shadow-broadcast" data-id="element-497">
            CREATE A CHARACTER. <br data-id="element-498" />
            <span className="text-white" data-id="element-499">GET PAID.</span>
          </h1>
          <p className="text-xl md:text-3xl text-slate font-medium mb-12 leading-tight max-w-3xl mx-auto" data-id="element-500">
            Your character. Your voice. Running in thousands of leagues. Paying
            you every single time it roasts someone.
          </p>

          <div className="bg-dark border-2 border-gold/30 rounded-2xl p-8 max-w-xl mx-auto shadow-[0_0_40px_rgba(245,197,24,0.15)]" data-id="element-501">
            <h3 className="font-heading font-black text-2xl uppercase tracking-wide mb-4 text-white" data-id="element-502">
              JOIN THE WAITLIST
            </h3>
            <p className="text-slate mb-6" data-id="element-503">
              We're opening submissions soon. Be the first to know.
            </p>
            <form className="flex flex-col sm:flex-row gap-4" onSubmit={e => e.preventDefault()} data-id="element-504">
              <input type="email" placeholder="Enter your email" className="flex-1 bg-charcoal border border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-gold transition-colors" data-id="element-505" />
              <button className="bg-gold text-charcoal px-8 py-3 rounded-xl font-black font-heading text-xl uppercase tracking-wide hover:bg-white hover:scale-105 transition-all" data-id="element-506">
                Notify Me
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 bg-dark border-y border-white/5" data-id="element-507">
        <div className="container mx-auto px-4" data-id="element-508">
          <SectionHeading title="HOW THE CREATOR ECONOMY WORKS" subtitle="Nobody in fantasy sports pays creators. We do." className="text-center mb-16" data-id="element-509" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-510">
            <motion.div whileHover={{
            y: -5
          }} className="bg-charcoal p-8 rounded-2xl border border-white/10 text-center" data-id="element-511">
              <div className="w-16 h-16 bg-blue-500/20 text-blue-400 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-512">
                <Sparkles size={32} data-id="element-513" />
              </div>
              <h4 className="font-heading font-black text-2xl uppercase tracking-wide mb-4 text-white" data-id="element-514">
                1. Design Your Character
              </h4>
              <p className="text-slate" data-id="element-515">
                Submit a voice description, personality profile, accent
                direction, and sample lines. Make it unhinged. Make it funny.
              </p>
            </motion.div>

            <motion.div whileHover={{
            y: -5
          }} className="bg-charcoal p-8 rounded-2xl border border-white/10 text-center" data-id="element-516">
              <div className="w-16 h-16 bg-orange/20 text-orange rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-517">
                <UploadCloud size={32} data-id="element-518" />
              </div>
              <h4 className="font-heading font-black text-2xl uppercase tracking-wide mb-4 text-white" data-id="element-519">
                2. Submit for Review
              </h4>
              <p className="text-slate" data-id="element-520">
                Our comedy team evaluates every submission. We're looking for
                unique voices that don't overlap with our existing cast.
              </p>
            </motion.div>

            <motion.div whileHover={{
            y: -5
          }} className="bg-charcoal p-8 rounded-2xl border border-gold/30 text-center relative overflow-hidden" data-id="element-521">
              <div className="absolute inset-0 bg-gold/5" data-id="element-522"></div>
              <div className="relative z-10" data-id="element-523">
                <div className="w-16 h-16 bg-gold/20 text-gold rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-524">
                  <DollarSign size={32} data-id="element-525" />
                </div>
                <h4 className="font-heading font-black text-2xl uppercase tracking-wide mb-4 text-gold" data-id="element-526">
                  3. Get Added & Get Paid
                </h4>
                <p className="text-slate" data-id="element-527">
                  Winners join the official cast permanently. You earn a
                  micro-royalty every single time your character runs in any
                  league.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ / DETAILS */}
      <section className="py-24 bg-charcoal" data-id="element-528">
        <div className="container mx-auto px-4 max-w-3xl text-center" data-id="element-529">
          <h2 className="text-4xl md:text-5xl font-black font-heading uppercase -skew-x-6 mb-8 text-white" data-id="element-530">
            WHAT MAKES A WINNING CHARACTER?
          </h2>
          <p className="text-xl text-slate mb-12" data-id="element-531">
            We don't need another generic sports bro. We need specificity. We
            need the guy who brings a clipboard to his son's flag football game.
            We need the grandmother who bets her pension on the over. We need
            characters so specific they feel real.
          </p>
          <p className="text-lg text-lime font-bold italic" data-id="element-532">
            "Don't be the next Ricky Rotardo. Be the voice that roasts him."
          </p>
        </div>
      </section>
    </div>;
};